/**
 * v0.8.51 — Household invite link helpers.
 *
 * Invite links look like `https://<host>/join?token=<48 hex chars>`. The
 * token is captured into localStorage on arrival so it survives the
 * sign-in round trip: a magic link opens `/auth/callback` (often in a new
 * tab), which would lose a sessionStorage value or a query param.
 * `PendingInviteHandler` redeems it once the user is signed in and clears
 * it whatever the outcome, so a bad token can't loop.
 */

export const PENDING_INVITE_KEY = 'debtos:pendingInvite:v1'

const TOKEN_PATTERN = /^[0-9a-f]{32,128}$/

export function buildInviteLink(origin: string, token: string): string {
  return `${origin.replace(/\/+$/, '')}/join?token=${encodeURIComponent(token)}`
}

/** Returns the token from a `?token=` query string, or null if absent/malformed. */
export function parseInviteToken(search: string): string | null {
  const raw = new URLSearchParams(search).get('token')?.trim().toLowerCase() ?? ''
  return TOKEN_PATTERN.test(raw) ? raw : null
}

export function savePendingInvite(token: string): void {
  try {
    localStorage.setItem(PENDING_INVITE_KEY, token)
  } catch {
    // Private mode / quota — the user can re-open the link after sign-in.
  }
}

export function getPendingInvite(): string | null {
  try {
    const raw = localStorage.getItem(PENDING_INVITE_KEY)
    return raw && TOKEN_PATTERN.test(raw) ? raw : null
  } catch {
    return null
  }
}

export function clearPendingInvite(): void {
  try {
    localStorage.removeItem(PENDING_INVITE_KEY)
  } catch {
    // ignore
  }
}
